import { OnCompleteAction, S3ReaderConfig } from 'ihub/common';
import { AdvancedTourStep } from '../shared-types';
import { TaskTourProps } from './task-tour-props';

export function getS3ReaderTourSteps({
	defaultStyles,
	microTaskConfigType,
}: Readonly<TaskTourProps>): AdvancedTourStep[] {
	if (!microTaskConfigType) return [];
	const config = microTaskConfigType as S3ReaderConfig;

	return [
		{
			id: 'aws-region',
			selector: '[data-tour="aws-region"]',
			content: 'Required: 	The AWS region that the S3 bucket was created in.',
		},
		{
			id: 'aws-access-key',
			selector: '[data-tour="aws-access-key"]',
			content: 'Required: The AWS Access Key ID that has access to the specified S3 bucket.',
		},
		{
			id: 'aws-secret-key',
			selector: '[data-tour="aws-secret-key"]',
			content: 'Required: The AWS Secret Access Key that has access to the S3 bucket.',
		},
		{
			id: 'aws-bucket-name',
			selector: '[data-tour="aws-bucket-name"]',
			content: 'Required: The AWS S3 bucket name.',
		},
		{
			id: 'test-connection',
			selector: '[data-tour="test-connection"]',
			content:
				'The button will conduct a connection test to see if the task has the needed permissions to perform its functions. This test has 3 steps.' +
				'1. Connect to the bucket.' +
				'2. Write a test file to the bucket and delete it.' +
				'3. Read a random object from the bucket.' +
				'Once the test is completed a report will show to indicate the available permissions.',
		},
		{
			id: 's3-object-filter',
			selector: '[data-tour="s3-object-filter"]',
			content:
				'The filter that will determine where and what files will be picked up from the S3 bucket. As seen in the example /ingest/Transactions_*.csv the filter can include a path on the S3 bucket as well as wildcards for a more dynamic filter. Supported wild cards are * that represents none or more characters, or # that represents exactly one character.',
		},
		{
			id: 'file-reader-config',
			selector: '[data-tour="file-reader-config"]',
			content: 'Select the type of file being read from AWS into iHub.',
		},
		{
			id: 'file-read-limit',
			selector: '[data-tour="file-read-limit"]',
			content: 'The maximum amount of files to read at a given time.',
		},
		{
			id: 'uniqueness-check',
			selector: '[data-tour="uniqueness-check"]',
			content:
				'This is used to set the duration of how long the data needs to be kept unique and prevent duplicate data.',
		},
		{
			id: 'unique-constraint',
			selector: '[data-tour="unique-constraint"]',
			content:
				'Set the unique constraint, this determines whether the value is unique based on the client/tenant/integration-template/project.',
		},
		{
			id: 'on-success-action',
			selector: '[data-tour="on-success-action"]',
			content: 'An action that will be performed when the task runs successfully.',
		},
		...(config.readerConfig.onSuccessAction === OnCompleteAction.Move
			? [
					{
						id: 'on-success-move',
						selector: '[data-tour="on-success-move"]',
						content: 'Set a destination path for the file/files to be moved to on successful read.',
					},
				]
			: []),
		{
			id: 'on-failure-action',
			selector: '[data-tour="on-failure-action"]',
			content: 'An action that will be performed should the file/files being read fail.',
		},
		...(config.readerConfig.onFailureAction === OnCompleteAction.Move
			? [
					{
						id: 'on-failure-move',
						selector: '[data-tour="on-failure-move"]',
						content: 'Set a destination path for the file/files to be moved to on failure.',
					},
				]
			: []),
	].map(tourItem => ({ ...tourItem, styles: defaultStyles }));
}
